import React, { useState } from 'react';
import './ModulePage.css';
import './LanguageSelector.css';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, validateLanguageSelection, normalizeLanguageName } from '../utils/languageValidation';

const LanguageSelector = ({ onSubmit, onCancel, showDialect }) => {
  const { t } = useLanguage();
  const [from, setFrom] = useState('en');
  const [to, setTo] = useState('es');
  const [dialect, setDialect] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [definitionMode, setDefinitionMode] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate language selection
    if (!definitionMode) {
      const validation = validateLanguageSelection(from, to);
      if (!validation.valid) {
        setError(t(validation.error) || validation.error);
        return;
      }
    }

    // Normalize language names
    const fromLanguage = normalizeLanguageName(from);
    const toLanguage = definitionMode ? fromLanguage : normalizeLanguageName(to);

    if (!fromLanguage || !toLanguage) {
      setError(t('invalidLanguageSelection') || 'Invalid language selection');
      return;
    }

    if (showDialect && !definitionMode && dialect.length > 50) {
      setError(t('dialectMaxLength') || 'Dialect cannot exceed 50 characters');
      return;
    }

    if (!name.trim()) {
      setError(t('moduleNameRequired') || 'Module name is required');
      return;
    }

    if (name.trim().length > 150) {
      setError(t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters');
      return;
    }

    if (showDialect) {
      onSubmit(fromLanguage, toLanguage, definitionMode ? '' : dialect.trim().substring(0, 50), name.trim().substring(0, 150));
    } else {
      onSubmit(fromLanguage, toLanguage, '', name.trim().substring(0, 150));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="language-selector-form">
      <div className="card learn-topic-card">
        <div className="form-group">
          <label>{definitionMode ? t('flashcardLanguage') : t('iSpeak')}</label>
          <select value={from} onChange={e => setFrom(e.target.value)}>
            {SUPPORTED_LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>{t(lang.name)}</option>
            ))}
          </select>
        </div>
        {!definitionMode && (
          <div className="form-group">
            <label>{t('iWantToLearn')}</label>
            <select value={to} onChange={e => setTo(e.target.value)}>
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{t(lang.name)}</option>
              ))}
            </select>
          </div>
        )}
        <div className="form-group" style={{ marginTop: 12, marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {t('moduleName') || 'Module Name'} *
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value.substring(0, 150))}
            placeholder={t('moduleNamePlaceholder') || 'e.g., Spanish Basics, French Conversation'}
            maxLength={150}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          />
          {name.length >= 150 && (
            <div style={{
              color: '#d32f2f',
              fontSize: '0.8rem',
              marginTop: '4px'
            }}>
              {t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters'}
            </div>
          )}
        </div>
        {showDialect && !definitionMode && (
          <div className="form-group" style={{ marginTop: 12, marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              {t('regionDialectOptional') || 'Region/Dialect (optional)'}
            </label>
            <input
              type="text"
              value={dialect}
              onChange={e => setDialect(e.target.value.substring(0, 50))}
              placeholder={t('dialectPlaceholder') || 'e.g., Mexican, Colombian, Castilian'}
              maxLength={50}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: '1rem'
              }}
            />
            {dialect.length >= 50 && (
              <div style={{
                color: '#d32f2f',
                fontSize: '0.8rem',
                marginTop: '4px'
              }}>
                {t('dialectMaxLength') || 'Dialect cannot exceed 50 characters'}
              </div>
            )}
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        {!definitionMode && from === to && !error && <div className="error-message">{t('pleaseSelectDifferentLanguages')}</div>}
        <div className="language-selector-actions">
          <button type="submit" className="btn btn-primary" disabled={!definitionMode && from === to}>{t('createModule')}</button>
          {onCancel && (
            <button 
              type="button" 
              className="btn btn-cancel" 
              onClick={() => {
                onCancel();
              }}
            >
              {t('cancel')}
            </button>
          )}
        </div>
        <button
          type="button"
          className="definition-mode-toggle"
          onClick={() => {
            setDefinitionMode(value => !value);
            setError('');
          }}
        >
          {definitionMode ? t('createLanguageModuleInstead') : t('createDefinitionModuleInstead')}
        </button>
      </div>
    </form>
  );
};

export default LanguageSelector;
